import { z } from 'zod'
import { env } from './env'

export const latestVersionKey = 'latestVersion'

const reloadRetryKey = 'versionReloadRetry'
/**
 * 配信の切り替え直後などはリロードしても新しい版が返らないことがあるため、間隔を空けて再挑戦する。
 * 判定は画面を表示するたびに行うので、タブを開いたまま使い続けても回数で諦めずに繰り返す。
 */
const firstRetryIntervalMs = 60 * 1000
const retryIntervalMs = 15 * 60 * 1000

const reloadRetrySchema = z.object({ count: z.number(), reloadedAt: z.number() })
type ReloadRetry = z.infer<typeof reloadRetrySchema>

export const appVersion = (): string => env('APP_TAG')

/**
 * 本番の API はタグ部分だけを返す（v1.2.3-45-gabc1234 -> v1.2.3）ため、タグ部分の一致も同じバージョンとみなす。
 * タグの付いていないコミットから配信された場合に、不一致のままリロードを繰り返さないようにする。
 */
export const isSameVersion = (currentVersion: string, latestVersion: string): boolean =>
  currentVersion === latestVersion || currentVersion.split('-')[0] === latestVersion

const parseReloadRetry = (stored: string | null): ReloadRetry | undefined => {
  if (!stored) return undefined
  try {
    const result = reloadRetrySchema.safeParse(JSON.parse(stored))
    return result.success ? result.data : undefined
  } catch {
    return undefined
  }
}

/**
 * @returns 今回リロードする場合は保存する試行状況。前回のリロードから間隔が空いていない場合は null
 */
export const nextReloadRetry = (stored: string | null, now: number): ReloadRetry | null => {
  const retry = parseReloadRetry(stored)
  if (!retry) {
    return { count: 1, reloadedAt: now }
  }
  const elapsed = now - retry.reloadedAt
  const interval = retry.count === 1 ? firstRetryIntervalMs : retryIntervalMs
  // 端末の時計が戻った場合に待ち続けないよう、経過時間が負なら間隔を満たしたものとして扱う
  return elapsed < 0 || elapsed >= interval ? { count: retry.count + 1, reloadedAt: now } : null
}

/**
 * @returns true: リロードした
 */
export const reloadIfVersionHasChanged = (): boolean => {
  const currentVersion = appVersion()
  const latestVersion = sessionStorage.getItem(latestVersionKey)
  // 最新バージョンは起動直後・リロード直後（API の応答前）や API が取得できなかったときに、
  // 自身のバージョンはビルドに埋め込まれていないときに分からない。比較できないため不一致として扱わない
  if (!currentVersion || !latestVersion) {
    return false
  }
  if (isSameVersion(currentVersion, latestVersion)) {
    sessionStorage.removeItem(reloadRetryKey)
    return false
  }

  try {
    const retry = nextReloadRetry(sessionStorage.getItem(reloadRetryKey), Date.now())
    if (!retry) {
      return false
    }
    sessionStorage.setItem(reloadRetryKey, JSON.stringify(retry))
  } catch (e) {
    // 前回のリロード時刻を残せないままリロードすると間隔を空けられず再読み込みを繰り返すため、更新を見送る
    console.error(e)
    return false
  }

  sessionStorage.removeItem(latestVersionKey)
  window.location.reload()
  return true
}
